import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

// import { ReactComponent as DeviceIcon } from '../../../../assets/icons/device-management/device-icon.svg';
import { ReactComponent as AlertIcon } from '../../../../../assets/icons/alert-icon.svg';
import { ReactComponent as CopyIcon } from '../../../../../assets/icons/copy.svg';
import { ReactComponent as ClearCacheIcon } from '../../../../../assets/icons/device-actions/clear-cache.svg';
import { ReactComponent as GetConfigIcon } from '../../../../../assets/icons/device-actions/get-config.svg';
import { ReactComponent as ResetIcon } from '../../../../../assets/icons/device-actions/reset.svg';
import { ReactComponent as RestartIcon } from '../../../../../assets/icons/device-actions/restart.svg';
import { ReactComponent as SendTriggerIcon } from '../../../../../assets/icons/device-actions/send-trigger.svg';
import { ReactComponent as UnlockConnectorIcon } from '../../../../../assets/icons/device-actions/unlock-connector.svg';
import { ReactComponent as StartCharging } from '../../../../../assets/icons/device-management/start-charging.svg';
import { ReactComponent as StopCharging } from '../../../../../assets/icons/device-management/stop-charging.svg';
import ElectricVehicleDevice from '../../../../../assets/images/ev-device.png';
import { allStatus, ocppMessageType } from '../../../../../config/enum';
import { ApiRequest } from '../../../../../interface/common.interface';
import {
  AvailableTabProperties,
  ConnectorProperties,
  DeviceDetailsProperties,
} from '../../../../../interface/device-management.interface';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { setSelectedDevice } from '../../../../../redux/slices/DeviceManagementSlice';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import ModalBox from '../../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../../shared-components/success-notification/SuccessNotification';
import SidePaneModel from '../../../../../shared-components/table-side-panel/SidePaneModel';
import TabsContainer from '../../../../../shared-components/tabs-container/TabsContainer';
import ConvertUTCtoDate from '../../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../../../shared-functions/ConvertUTCtoHHMM';
import FormatDuration from '../../../../../shared-functions/FormatDuration';
import FormatUtcString from '../../../../../shared-functions/FormatUtcString';
// import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import device from '../../../../../utils/api/device';
import deviceHubApi from '../../../../../utils/api/device-hub';
import deviceMetadataApi from '../../../../../utils/api/device-metadata';
import imagesApi from '../../../../../utils/api/images';
import ocppEvents from '../../../../../utils/api/ocpp-events';
import OcppConsole from '../ocpp-console/OcppConsole';

// import EditDeviceForm from './EditDeviceForm';

// import DeviceMap from './DeviceMap';

/**
 * Connector menu properties interface
 */
interface ConnectorMenuProperties {
  connectorId: number;
  isActive: boolean;
}

/**
 * Connector history properties interface
 */
interface ConnectorHistoryProperties {
  id: string;
  _id: string;
  date: string;
  time: string;
  amount: string;
  powerConsumed: string;
  transactionStartsAt: string;
}

/**
 * Device content tabs properties interface
 */
interface DeviceContentTabsProperties {
  selectedDeviceGeneral: DeviceDetailsProperties;
  setSelectedDeviceGeneral: React.Dispatch<React.SetStateAction<DeviceDetailsProperties>>;
}

/**
 * Charging point metadata filter interface
 */
interface ChargingPointMetadataFilter {
  modelSeries: string;
}

/**
 * Media interface
 */
interface MediaInterface {
  url: string;
  fileType: string;
  fileSize: string;
  fileName: string;
}

/**
 * Charging point metadata interface
 */
interface ChargingPointMetadata {
  defaultMedias: MediaInterface[];
}

/**
 * Calculates the number of whole minutes elapsed since the given ISO timestamp.
 * @param {string} [isoDate] - The ISO 8601 timestamp to compare against the current time.
 * @returns {number} The number of whole minutes elapsed, or 0 if the timestamp is missing or invalid.
 */
function getMinutesSince(isoDate?: string): number {
  if (!isoDate) return 0;
  const pastTime = new Date(isoDate).getTime();
  if (Number.isNaN(pastTime)) return 0;
  return Math.floor((Date.now() - pastTime) / 60000);
}

/**
 * Extracts a plain ISO date string from a value that may be either a plain
 * string or a MongoDB extended-JSON `{ $date: string }` wrapper.
 * @param {string | { $date: string }} [value] - The raw date value.
 * @returns {string} The extracted ISO date string, or an empty string if missing.
 */
function extractDateString(value?: string | { $date: string }): string {
  if (!value) return '';
  return typeof value === 'string' ? value : value.$date;
}

/**
 * Maps an OCPP configuration status string to a badge modifier class.
 * @param {string} [status] - The configuration status (e.g. "Accepted").
 * @returns {string} The badge modifier class name.
 */
function getConfigStatusClass(status?: string): string {
  const normalized = status?.toLowerCase();
  if (normalized === 'accepted') return 'status-accepted';
  if (normalized === 'rejected') return 'status-rejected';
  if (normalized === 'rebootrequired') return 'status-reboot-required';
  if (normalized === 'notsupported') return 'status-not-supported';
  return 'status-unknown';
}

/**
 * Component to display the tabs of the device content
 * @param {any} properties - The properties
 * @returns {any} The result
 */
function DeviceContentTabs(properties: Readonly<DeviceContentTabsProperties>) {
  const { t, i18n } = useTranslation('devices');
  const location = useLocation();
  const hasDeviceHub = location.pathname.includes('ev-device-hub/device-details');
  const { id } = useParams<{ id: string; type: string; userCustomId: string }>();
  const [, setSearchParameters] = useSearchParams();
  const { selectedDeviceGeneral, setSelectedDeviceGeneral } = properties;
  const { selectedChargingPoint } = useAppSelector((state) => state.device);
  const dispatch = useAppDispatch();
  let isDeviceOffline: boolean | string = true;
  if (selectedDeviceGeneral?.connectionStatus) {
    isDeviceOffline = selectedDeviceGeneral.connectionStatus.toLowerCase() !== 'online';
  } else if (
    selectedDeviceGeneral?.isDeviceOffline !== undefined &&
    selectedDeviceGeneral?.isDeviceOffline !== null
  ) {
    isDeviceOffline = selectedDeviceGeneral.isDeviceOffline;
  }
  const [, forceOfflineTick] = useState(0);
  const [sortOptions, setSortOptions] = useState<Record<string, 'asc' | 'desc'>>({
    date: 'desc',
    time: 'desc',
    amount: 'desc',
    powerConsumed: 'desc',
  });
  const [, setConnectorHistory] = useState<ConnectorHistoryProperties[]>([]);
  const [activeTabKey, setActiveTabKey] = useState('basicInfo');

  const containerTabs: AvailableTabProperties[] = [
    {
      label: t('basicInfo', 'Basic Info'),
      key: 'basicInfo',
      isActive: activeTabKey === 'basicInfo',
    },
    {
      label: t('technicalInfo', 'Technical Info'),
      key: 'technicalInfo',
      isActive: activeTabKey === 'technicalInfo',
    },
    {
      label: t('controls', 'Controls'),
      key: 'controls',
      isActive: activeTabKey === 'controls',
    },
    {
      label: t('configuration', 'Configuration'),
      key: 'configuration',
      isActive: activeTabKey === 'configuration',
    },
  ];

  const [connectorMenu, setConnectorMenu] = useState<ConnectorMenuProperties[]>([]);
  const [availableConnectors, setAvailableConnectors] = useState<ConnectorProperties[]>([]);

  const [deviceMetadata, setDeviceMetadata] = useState<ChargingPointMetadata>({
    defaultMedias: [],
  });
  const [deviceImageUrl, setDeviceImageUrl] = useState<string>('');
  const [modalView, setModalView] = useState({
    status: false,
    title: '',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: true,
  });

  const [sidePanelState, setSidePanelState] = useState<{
    open: boolean;
    title: string;
    content: JSX.Element | null;
  }>({
    open: false,
    title: '',
    content: null,
  });

  const basicInfoDetails = [
    { id: 1, label: t('name', 'Name'), key: 'chargingPointName' },
    { id: 2, label: t('serialNumber', 'Serial Number'), key: 'chargingPointSn' },
    ...(hasDeviceHub
      ? []
      : [
          { id: 3, label: t('stationName', 'Station Name'), key: 'stationName' },
          { id: 4, label: t('partnerName', 'Partner Name'), key: 'partnerName' },
        ]),
    { id: 5, label: t('connectorCounts', 'Connector Counts'), key: 'connectors' },
    { id: 6, label: t('deviceModel', 'Charger Model'), key: 'chargePointModel' },
    { id: 7, label: t('chargePointVendor', 'Charger Vendor'), key: 'chargePointVendor' },
    { id: 8, label: t('skuNumber', 'SKU Number'), key: 'skuNumber' },
    { id: 9, label: t('networkConnectivity', 'Network Connectivity'), key: 'networkConnectivity' },
    { id: 10, label: t('accessStatus', 'Web socket Url'), key: 'deviceAccessStatus' },
    { id: 11, label: t('websocketUrl', 'Web socket Url'), key: 'ocppurl', copy: true },
  ];

  const techInfoDetails = [
    { id: 1, label: t('firmwareVersion', 'Firmware Version'), key: 'firmwareVersion' },
    { id: 2, label: t('currentType', 'Current Type'), key: 'currentType' },
    { id: 3, label: t('installationType', 'Installation Type'), key: 'installationType' },
    { id: 4, label: t('maxPowerAvailable', 'Max Power Available'), key: 'maxPowerAvailable' },
    { id: 5, label: t('protocolSupport', 'Protocol Support'), key: 'protocolSupport' },
    { id: 6, label: t('availabilityStatus', 'Availability Status'), key: 'isAvailable' },
    { id: 7, label: t('connectionStatus', 'Connection Status'), key: 'isDeviceOffline' },
    { id: 8, label: t('plugAndCharge', 'Plug and Charge'), key: 'plugAndCharge' },
  ];

  const minutesSinceLastOnline = getMinutesSince(selectedDeviceGeneral?.lastOnlineAt);
  const showOfflineAlert =
    Boolean(isDeviceOffline) &&
    Boolean(selectedDeviceGeneral?.firstConnectedAt) &&
    Boolean(selectedDeviceGeneral?.lastOnlineAt) &&
    minutesSinceLastOnline > 30;

  /**
   * Function to copy value to clipboard and show notification toast
   * @param {string} value - The value to copy
   * @returns {Promise<void>} Resolves when text is copied
   */
  const handleCopy = async (value: string): Promise<void> => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('copied', 'Copied!'),
          type: 'success',
          code: 0,
        }),
      );
    } catch (error: any) {
      console.error(error);
    }
  };

  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: false,
      content: <div />,
      rightCorner: '',
      notification: false,
    });
  };

  /**
   * Side panel close function.
   */
  const closeSidePanel = () => {
    setSidePanelState({
      open: false,
      title: '',
      content: null,
    });
    const queryParameters = new URLSearchParams(location.search);
    queryParameters.delete('operation');
    queryParameters.delete('resetType');
    queryParameters.delete('hideSidebar');
    setSearchParameters(queryParameters);
  };

  /**
   * Completed the update task
   * @param {any} message - The message
   */
  const ShowSuccessNotification = (message: string) => {
    setModalView({
      status: true,
      title: '',
      header: false,
      content: (
        <SuccessNotification
          title={message}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={closeModal}
        />
      ),
      rightCorner: '',
      notification: true,
    });
  };

  /**
   * Handle Connector Click Event
   * @param {any} connectorId - The connectorId
   * @param {any} clickType - The clickType
   */
  const handleConnectorClickEvent = async (
    connectorId: string | number,
    clickType: 'start' | 'stop',
  ) => {
    if (isDeviceOffline) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: t(
            'unableToTriggerOffline',
            'Unable to trigger this action because device is offline',
          ),
          type: 'error',
          code: 0,
        }),
      );
      return;
    }
    try {
      if (clickType === 'start') {
        await ocppEvents.anyCommand({
          type: ocppMessageType.RMT_START_TRANSACTION,
          body: {
            connectorId: Number(connectorId),
          },
          chargingPointSns: [selectedChargingPoint?.chargingPointSn],
        });
        setAvailableConnectors((previous) =>
          previous?.map((connector) =>
            connector.connectorId === Number(connectorId)
              ? {
                  ...connector,
                  status: allStatus.CHARGING,
                }
              : connector,
          ),
        );
        ShowSuccessNotification('Start charging requested');
      } else if (clickType === 'stop') {
        await ocppEvents.stopTransaction({
          chargingPointSn: selectedChargingPoint?.chargingPointSn,
          connectorId: Number(connectorId),
        });
        setAvailableConnectors((previous) =>
          previous?.map((connector) =>
            connector.connectorId === Number(connectorId)
              ? {
                  ...connector,
                  status: allStatus.FINISHED,
                }
              : connector,
          ),
        );
        ShowSuccessNotification('Stop charging requested');
      }
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Opens the OCPP Console in a side panel with the pre-selected operation.
   * @param {string} operationType - The type of operation/command to pre-select.
   * @param {string} [resetType] - Optional reset type (Hard/Soft).
   */
  const handleOpenOcppSidePanel = (operationType: string, resetType?: string) => {
    if (isDeviceOffline) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: t(
            'unableToTriggerOffline',
            'Unable to trigger this action because device is offline',
          ),
          type: 'error',
          code: 0,
        }),
      );
      return;
    }
    const queryParameters = new URLSearchParams(location.search);
    queryParameters.set('operation', operationType);
    queryParameters.set('hideSidebar', 'true');
    if (resetType) {
      queryParameters.set('resetType', resetType);
    } else {
      queryParameters.delete('resetType');
    }
    setSearchParameters(queryParameters);

    setSidePanelState({
      open: true,
      title: t('oCPPConsole', 'OCPP Console'),
      content: <OcppConsole />,
    });
  };

  const deviceControls = [
    {
      id: 1,
      label: t('restart'),
      svgIcon: <RestartIcon />,
      type: ocppMessageType.RESET_CP,
      /**
       * On-click functionality
       */
      onClickFunc: () => handleOpenOcppSidePanel(ocppMessageType.RESET_CP, 'Soft'),
    },
    {
      id: 2,
      label: t('reset'),
      svgIcon: <ResetIcon />,
      /**
       * On-click functionality
       */
      onClickFunc: () => handleOpenOcppSidePanel(ocppMessageType.RESET_CP, 'Hard'),
      type: ocppMessageType.RESET_CP,
    },
    {
      id: 3,
      label: t('sendTrigger'),
      svgIcon: <SendTriggerIcon />,
      /**
       * On-click functionality
       */
      onClickFunc: () => handleOpenOcppSidePanel(ocppMessageType.TRIGGER_MESSAGE),
      type: ocppMessageType.TRIGGER_MESSAGE,
    },
    {
      id: 4,
      label: t('unlockConnector'),
      svgIcon: <UnlockConnectorIcon />,
      /**
       * On-click functionality
       */
      onClickFunc: () => handleOpenOcppSidePanel(ocppMessageType.UNLOCK_CONNECTOR),
      type: ocppMessageType.UNLOCK_CONNECTOR,
    },
    {
      id: 5,
      label: t('getConfiguration'),
      svgIcon: <GetConfigIcon />,
      /**
       * On-click functionality
       */
      onClickFunc: () => handleOpenOcppSidePanel(ocppMessageType.GET_CONFIGURATION),
      type: ocppMessageType.GET_CONFIGURATION,
    },
    {
      id: 6,
      label: t('clearCache'),
      svgIcon: <ClearCacheIcon />,
      /**
       * On-click functionality
       */
      onClickFunc: () => handleOpenOcppSidePanel(ocppMessageType.CLEAR_CACHE),
      type: ocppMessageType.CLEAR_CACHE,
    },
    {
      id: 7,
      label: t('remoteStart'),
      svgIcon: <StartCharging />,
      /**
       * On-click functionality
       */
      onClickFunc: () => handleOpenOcppSidePanel(ocppMessageType.RMT_START_TRANSACTION),
      type: ocppMessageType.RMT_START_TRANSACTION,
    },
    {
      id: 8,
      label: t('remoteStop'),
      svgIcon: <StopCharging />,
      /**
       * On-click functionality
       */
      onClickFunc: () => handleOpenOcppSidePanel(ocppMessageType.RMT_STOP_TRANSACTION),
      type: ocppMessageType.RMT_STOP_TRANSACTION,
    },
  ];

  // const column = [
  //   {
  //     label: t('date'),
  //     accessorKey: 'date' as keyof ConnectorHistoryProperties,
  //     sortAllow: true,
  //   },
  //   {
  //     label: t('time'),
  //     accessorKey: 'time' as keyof ConnectorHistoryProperties,
  //     sortAllow: true,
  //   },
  //   {
  //     label: t('amount'),
  //     accessorKey: 'amount' as keyof ConnectorHistoryProperties,
  //     sortAllow: true,
  //     /**
  //      * Function to show unit with amount
  //      * @param rowData
  //      * @returns
  //      */
  //     customData: (rowData: ConnectorHistoryProperties) => (
  //       <p>{ToFixedValue(rowData?.amount || 0)}</p>
  //     ),
  //   },
  //   {
  //     label: t('energyConsumed'),
  //     accessorKey: 'powerConsumed' as keyof ConnectorHistoryProperties,
  //     sortAllow: true,
  //     /**
  //      * Function to show unit with power conception
  //      * @param rowData
  //      * @returns
  //      */
  //     customData: (rowData: ConnectorHistoryProperties) => (
  //       <p>{ToFixedValue(Number(rowData?.powerConsumed || 0) / 100)} Kwh</p>
  //     ),
  //   },
  // ];

  /**
   * Function to switch the tab
   * @param {string} key - The key
   */
  const handleSwitchTab = (key: string) => {
    setActiveTabKey(key);
  };

  /**
   * General details
   * @param {any} deviceId - The deviceId
   */
  const handleFetchDeviceGeneralDetails = async (deviceId: string) => {
    try {
      const isDeviceHub = location.pathname.includes('ev-device-hub');
      if (isDeviceHub) {
        const response = await deviceHubApi.fetchAdvanceDeviceDetails(deviceId);
        const data = response?.data || response || {};

        const connectorsMapped = (data.connectorInformation || []).map(
          (conn: any, index: number) => {
            let ratingValue = '';
            if (conn.connectorRating) {
              ratingValue =
                typeof conn.connectorRating === 'object'
                  ? conn.connectorRating.capacityKW
                  : conn.connectorRating;
            }
            return {
              ...conn,
              connectorId: conn.connectorId || index + 1,
              status: conn.status || 'unknown',
              connectorRating: ratingValue,
            };
          },
        );

        const stationNameValue =
          data.stationName ||
          data.chargingStationName ||
          data.chargingStation?.name ||
          data.chargingStation?.stationName ||
          data.chargingStation?.chargingStationName ||
          '';
        const partnerNameValue =
          data.partnerName ||
          data.accountName ||
          data.account?.accountName ||
          data.account?.name ||
          data.partner?.partnerName ||
          data.partner?.name ||
          data.chargingStation?.accountName ||
          data.chargingStation?.account?.accountName ||
          data.chargingStation?.account?.name ||
          data.chargingStation?.partnerName ||
          data.chargingStation?.partner?.name ||
          data.cpoName ||
          data.tenantName ||
          data.tenantId ||
          '';

        setSelectedDeviceGeneral({
          ...data,
          id: data._id || data.id,
          chargingPointName: data.serialNumber || '',
          chargingPointSn: data.serialNumber || '',
          chargePointVendor: data.brandName || '',
          chargePointModel: data.modelName || '',
          vendor: data.brandName || '',
          model: data.modelName || '',
          maxPowerAvailable: data.specifications?.powerOutput
            ? `${Number(data.specifications.powerOutput).toFixed(3)} kW`
            : '',
          maximumOutputPower: data.specifications?.powerOutput
            ? String(data.specifications.powerOutput)
            : '0',
          maximumOutputPowerUnit: 'kW',
          serialNumber: data.serialNumber || '',
          tenantId: data.tenantName || '',
          reachableState: data.status || '',
          positionType: data.installationType || '',
          currentType: data.currentType || '',
          connectors: connectorsMapped,
          stationName: stationNameValue,
          partnerName: partnerNameValue,
        });
        dispatch(
          setSelectedDevice({
            id: data._id || data.id || deviceId,
            name: data.serialNumber || '',
            chargingPointSn: data.serialNumber || '',
            modelSeries: data.modelSeries || '',
          }),
        );
      } else {
        const response = await device.fetchChargingPointGeneralDetails(deviceId);
        const enelData = response?.data?.enelData || {};
        const stationNameValue =
          response?.data?.stationName ||
          response?.data?.chargingStationName ||
          response?.data?.chargingStation?.name ||
          response?.data?.chargingStation?.stationName ||
          response?.data?.chargingStation?.chargingStationName ||
          '';
        const partnerNameValue =
          response?.data?.partnerName ||
          response?.data?.accountName ||
          response?.data?.account?.accountName ||
          response?.data?.account?.name ||
          response?.data?.partner?.partnerName ||
          response?.data?.partner?.name ||
          response?.data?.chargingStation?.accountName ||
          response?.data?.chargingStation?.account?.accountName ||
          response?.data?.chargingStation?.account?.name ||
          response?.data?.chargingStation?.partnerName ||
          response?.data?.chargingStation?.partner?.name ||
          response?.data?.cpoName ||
          response?.data?.tenantName ||
          response?.data?.tenantId ||
          '';
        setSelectedDeviceGeneral({
          ...response.data,
          stationName: stationNameValue,
          partnerName: partnerNameValue,
          maxPowerAvailable: response?.data?.maximumOutputPower
            ? `${Number(response.data.maximumOutputPower).toFixed(3)} ${response.data.maximumOutputPowerUnit || ''}`
            : '',
          serialNumber: enelData.sn !== undefined ? String(enelData.sn) : undefined,
          tenantId: enelData.tenantId !== undefined ? String(enelData.tenantId) : undefined,
          maxPowerPoc:
            enelData.powerMaxPoc !== undefined ? String(enelData.powerMaxPoc) : undefined,
          reachableState:
            enelData.isReachableState !== undefined ? String(enelData.isReachableState) : undefined,
          acVoltage:
            enelData.acElectricCurrent?.voltage !== undefined
              ? String(enelData.acElectricCurrent.voltage)
              : undefined,
          positionType:
            enelData.csTechnicalConfiguration?.address?.position?.type !== undefined
              ? String(enelData.csTechnicalConfiguration.address.position.type)
              : undefined,
          rvxg: enelData.isRVxG !== undefined ? String(enelData.isRVxG) : undefined,
          rvxgAlgorithm:
            enelData.rvxgAlgorithm !== undefined ? String(enelData.rvxgAlgorithm) : undefined,
          rvxgLoadAreaId:
            enelData.rvxgLoadAreaId !== undefined ? String(enelData.rvxgLoadAreaId) : undefined,
          rvxgLoadAreaName:
            enelData.rvxgLoadAreaName !== undefined ? String(enelData.rvxgLoadAreaName) : undefined,
        });
        const sn =
          response?.data?.chargingPointSn ||
          response?.data?.serialNumber ||
          (enelData.sn !== undefined ? String(enelData.sn) : '');
        dispatch(
          setSelectedDevice({
            id: response?.data?._id || response?.data?.id || deviceId,
            name: response?.data?.chargingPointName || response?.data?.chargingPointSn || '',
            chargingPointSn: sn,
            modelSeries: response?.data?.modelSeries || '',
          }),
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Device Metadata
   * @param {any} filter - The filter
   */
  const handleFetchDeviceMetadata = async (filter: ChargingPointMetadataFilter) => {
    try {
      const response = await deviceMetadataApi.fetchChargingPointMetadata(filter);
      setDeviceMetadata(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Fetch signed device image URL for given device serial number
   * @param {string} deviceSN - The device serial number
   */
  const handleFetchDeviceImage = async (deviceSN: string) => {
    if (location.pathname.includes('/ev-device-hub/device-details')) {
      return;
    }
    try {
      const response = await imagesApi.getDeviceImage(deviceSN);
      const imageDetail = response?.data?.imageUrls?.[0];
      setDeviceImageUrl(imageDetail?.signedUrl || imageDetail?.url || '');
    } catch (error) {
      console.log(error);
    }
  };

  // To-do -> Need additional inputs for start and stop charging

  /**
   * Connector selection change
   * @param {any} options - The options
   */
  // const handleSelectConnector = (selectIndex: number) => {
  //   setConnectorMenu((previousValue) =>
  //     previousValue.map((eachValue, index) => {
  //       let updatedValue;
  //       if (index === selectIndex) {
  //         updatedValue = {
  //           ...eachValue,
  //           isActive: true,
  //         };
  //       } else {
  //         updatedValue = {
  //           ...eachValue,
  //           isActive: false,
  //         };
  //       }
  //       return updatedValue;
  //     }),
  //   );
  // };

  /**
   * Handle fetch connector history
   */
  const handleFetchConnectorHistory = async (options?: ApiRequest) => {
    const isDeviceHub = location.pathname.includes('ev-device-hub');
    if (isDeviceHub || !selectedChargingPoint?.chargingPointSn) return;
    try {
      const parameters: any = {
        chargingPointSn: selectedChargingPoint?.chargingPointSn,
        connector: connectorMenu.findIndex((menu) => menu.isActive) + 1,
      };

      const sortField = options?.sortField || null;

      if (sortField) {
        // Get current sort for this column
        const currentSort = sortOptions[sortField] || 'asc';

        // Determine new sort order
        let sortBy: 'asc' | 'desc' = 'asc';
        if (options?.sortField === sortField) {
          sortBy = currentSort === 'asc' ? 'desc' : 'asc';
        }

        parameters.sortField = sortField;
        parameters.sortBy = sortBy;

        // Update state for that column
        setSortOptions((previous) => ({
          ...previous,
          [sortField]: sortBy,
        }));
      }

      const response = await device.fetchConnectorHistory(parameters);

      setConnectorHistory(
        response.data.map((eachData: ConnectorHistoryProperties) => ({
          ...eachData,
          id: eachData._id,
          date: ConvertUTCtoDate(eachData.transactionStartsAt),
          time: FormatUtcString(ConvertUTCtoHHMM(eachData.transactionStartsAt)),
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (connectorMenu.length) {
      handleFetchConnectorHistory();
    }
  }, [connectorMenu]);

  /**
   * sort Filter function
   * @param value
   */
  // const pageNateLimitChange = (value: ApiRequest) => {
  //   handleFetchConnectorHistory(value);
  // };

  useEffect(() => {
    const activeId = id || selectedChargingPoint?.id;
    if (activeId) {
      handleFetchDeviceGeneralDetails(activeId);
    }
  }, [id, selectedChargingPoint?.id]);

  useEffect(() => {
    const interval = setInterval(() => forceOfflineTick((tick) => tick + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const modelSeries =
      selectedChargingPoint?.modelSeries || (selectedDeviceGeneral as any)?.modelSeries;
    if (modelSeries) {
      handleFetchDeviceMetadata({
        modelSeries,
      });
    }
  }, [selectedChargingPoint?.modelSeries, (selectedDeviceGeneral as any)?.modelSeries]);

  useEffect(() => {
    const deviceSN =
      selectedChargingPoint?.chargingPointSn || selectedDeviceGeneral?.chargingPointSn;
    if (deviceSN) {
      handleFetchDeviceImage(deviceSN);
    }
  }, [selectedChargingPoint?.chargingPointSn, selectedDeviceGeneral?.chargingPointSn]);

  useEffect(() => {
    if (selectedDeviceGeneral.connectors.length) {
      setConnectorMenu(
        Array.from({ length: selectedDeviceGeneral.connectors.length }, (_, index) => ({
          connectorId: index + 1,
          isActive: index === 0, // First object is active
        })),
      );
    }
  }, [selectedDeviceGeneral]);

  useEffect(() => {
    if (selectedDeviceGeneral?.connectors?.length) {
      setAvailableConnectors(selectedDeviceGeneral?.connectors);
    }
  }, [selectedDeviceGeneral]);

  return (
    <div className="device-content-tabs-wrap" dir={i18n.dir()}>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <SidePaneModel
        open={sidePanelState.open}
        title={sidePanelState.title}
        content={sidePanelState.content ?? <div />}
        closePanel={closeSidePanel}
      />
      <div className="device-tabs-header-wrap">
        <TabsContainer containerTabs={containerTabs} handleSwitchTab={handleSwitchTab} />
        {showOfflineAlert && (
          <div className="device-offline-alert-banner">
            <AlertIcon className="alert-icon" />
            <div className="alert-content">
              <p className="alert-title">{t('deviceOfflineAlertTitle', 'Device Offline')}</p>
              <p className="alert-description">
                {t(
                  'deviceOfflineAlertDescription',
                  'This device has been unreachable for {{duration}}.',
                  {
                    duration: FormatDuration(minutesSinceLastOnline, {
                      formatBy: 'min',
                      showDays: true,
                    }),
                  },
                )}
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="device-content">
        {(() => {
          const activeTab = containerTabs.find((tab) => tab.isActive);
          let dataToShow = null;
          if (activeTab) {
            switch (activeTab.key) {
              case 'basicInfo':
                dataToShow = (
                  <div className="device-general-details-wrap">
                    <div className="device-image-section-wrap">
                      <div className="device-general-image-container">
                        <img
                          src={
                            (selectedDeviceGeneral as any)?.imageUrls?.[0]?.signedUrl ||
                            (selectedDeviceGeneral as any)?.medias?.[0]?.signedUrl ||
                            (deviceMetadata as any)?.imageUrls?.[0]?.signedUrl ||
                            deviceImageUrl ||
                            deviceMetadata?.defaultMedias?.[0]?.url ||
                            ElectricVehicleDevice
                          }
                          alt=""
                          className="device-details-image"
                          loading="lazy"
                        />
                        <div
                          className={`device-status-container ${isDeviceOffline ? 'offline' : 'online'}`}>
                          <div className="static-container" />
                          <div className="animation-container" />
                        </div>
                        <div className="device-status-text-wrap">
                          <div
                            className={`device-status-text-container ${isDeviceOffline ? 'offline' : 'online'}`}>
                            <div className="icon" />
                            <p>{isDeviceOffline ? t('offline') : t('online')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="device-general-details-container">
                      {basicInfoDetails.map((generalDetail) => {
                        let value =
                          selectedDeviceGeneral[generalDetail.key as keyof DeviceDetailsProperties];

                        if (generalDetail.key === 'stationName') {
                          value =
                            selectedDeviceGeneral.stationName ||
                            (selectedDeviceGeneral as any).chargingStationName ||
                            (selectedDeviceGeneral as any).chargingStation?.name ||
                            (selectedDeviceGeneral as any).chargingStation?.stationName ||
                            (selectedDeviceGeneral as any).chargingStation?.chargingStationName;
                        } else if (generalDetail.key === 'partnerName') {
                          value =
                            (selectedDeviceGeneral as any).partnerName ||
                            (selectedDeviceGeneral as any).accountName ||
                            (selectedDeviceGeneral as any).account?.accountName ||
                            (selectedDeviceGeneral as any).account?.name ||
                            (selectedDeviceGeneral as any).partner?.partnerName ||
                            (selectedDeviceGeneral as any).partner?.name ||
                            (selectedDeviceGeneral as any).chargingStation?.accountName ||
                            (selectedDeviceGeneral as any).chargingStation?.account?.accountName ||
                            (selectedDeviceGeneral as any).chargingStation?.account?.name ||
                            (selectedDeviceGeneral as any).chargingStation?.partnerName ||
                            (selectedDeviceGeneral as any).chargingStation?.partner?.name ||
                            (selectedDeviceGeneral as any).cpoName ||
                            (selectedDeviceGeneral as any).tenantName ||
                            (selectedDeviceGeneral as any).tenantId;
                        }
                        let displayValue = '-';
                        if (generalDetail.key === 'isDeviceOffline') {
                          displayValue = isDeviceOffline
                            ? t('offline', 'Offline')
                            : t('online', 'Online');
                        } else if (value !== undefined && value !== null && value !== '') {
                          if (generalDetail.key === 'connectors') {
                            displayValue = Array.isArray(value) ? String(value.length) : '0';
                          } else if (generalDetail.key === 'isAvailable') {
                            displayValue = value
                              ? t('available', 'Available')
                              : t('unavailable', 'Unavailable');
                          } else if (generalDetail.key === 'plugAndCharge') {
                            displayValue = value ? t('yes', 'Yes') : t('no', 'No');
                          } else {
                            displayValue = String(value);
                          }
                        }

                        return (
                          <div className="each-general-detail" key={generalDetail.key}>
                            <div className="general-detail-content">
                              <h1>{displayValue}</h1>
                              <h6>{generalDetail.label}</h6>
                            </div>
                            {generalDetail.copy && (
                              <CopyIcon
                                className="copy-icon"
                                onClick={() => handleCopy(displayValue)}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
                break;
              case 'technicalInfo':
                dataToShow = (
                  <div className="device-general-details-wrap">
                    <div className="device-general-image-container">
                      <img
                        src={
                          (selectedDeviceGeneral as any)?.imageUrls?.[0]?.signedUrl ||
                          (selectedDeviceGeneral as any)?.medias?.[0]?.signedUrl ||
                          (deviceMetadata as any)?.imageUrls?.[0]?.signedUrl ||
                          deviceImageUrl ||
                          deviceMetadata?.defaultMedias?.[0]?.url ||
                          ElectricVehicleDevice
                        }
                        alt=""
                        className="device-details-image"
                        loading="lazy"
                      />
                      <div
                        className={`device-status-container ${isDeviceOffline ? 'offline' : 'online'}`}>
                        <div className="static-container" />
                        <div className="animation-container" />
                      </div>
                      <div className="device-status-text-wrap">
                        <div
                          className={`device-status-text-container ${isDeviceOffline ? 'offline' : 'online'}`}>
                          <div className="icon" />
                          <p>{isDeviceOffline ? t('offline') : t('online')}</p>
                        </div>
                      </div>
                    </div>
                    <div className="device-general-details-container">
                      {techInfoDetails.map((techDetail) => {
                        const value =
                          selectedDeviceGeneral[techDetail.key as keyof DeviceDetailsProperties];
                        let displayValue = '-';
                        if (techDetail.key === 'isDeviceOffline') {
                          displayValue = isDeviceOffline
                            ? t('offline', 'Offline')
                            : t('online', 'Online');
                        } else if (value !== undefined && value !== null && value !== '') {
                          if (techDetail.key === 'isAvailable') {
                            displayValue = value
                              ? t('available', 'Available')
                              : t('unavailable', 'Unavailable');
                          } else if (techDetail.key === 'plugAndCharge') {
                            displayValue = value ? t('yes', 'Yes') : t('no', 'No');
                          } else {
                            displayValue = String(value);
                          }
                        }

                        return (
                          <div className="each-general-detail" key={techDetail.key}>
                            <div className="general-detail-content">
                              <h1>{displayValue}</h1>
                              <h6>{techDetail.label}</h6>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
                break;
              case 'controls':
                dataToShow = (
                  <div className="device-controls-wrap">
                    <div className="device-general-image-container">
                      <img
                        src={
                          (selectedDeviceGeneral as any)?.imageUrls?.[0]?.signedUrl ||
                          (selectedDeviceGeneral as any)?.medias?.[0]?.signedUrl ||
                          (deviceMetadata as any)?.imageUrls?.[0]?.signedUrl ||
                          deviceImageUrl ||
                          deviceMetadata?.defaultMedias?.[0]?.url ||
                          ElectricVehicleDevice
                        }
                        alt=""
                        className="device-details-image"
                        loading="lazy"
                      />
                      <div
                        className={`device-status-container ${isDeviceOffline ? 'offline' : 'online'}`}>
                        <div className="static-container" />
                        <div className="animation-container" />
                      </div>
                      <div className="device-status-text-wrap">
                        <div
                          className={`device-status-text-container ${isDeviceOffline ? 'offline' : 'online'}`}>
                          <div className="icon" />
                          <p>{isDeviceOffline ? t('offline') : t('online')}</p>
                        </div>
                      </div>
                    </div>
                    <div className="device-controls-container">
                      {deviceControls.map((deviceControl) => {
                        return (
                          <div
                            className={`device-control ${isDeviceOffline ? 'disabled' : ''}`}
                            key={deviceControl.id}
                            role="presentation"
                            title={
                              isDeviceOffline
                                ? t(
                                    'unableToTriggerOffline',
                                    'Unable to trigger this action because device is offline',
                                  )
                                : ''
                            }
                            onClick={() => {
                              if (isDeviceOffline) {
                                dispatch(
                                  setNotificationMessage({
                                    status: true,
                                    message: t(
                                      'unableToTriggerOffline',
                                      'Unable to trigger this action because device is offline',
                                    ),
                                    type: 'error',
                                    code: 0,
                                  }),
                                );
                                return;
                              }
                              deviceControl.onClickFunc();
                            }}>
                            <div className="device-control-icon">{deviceControl.svgIcon}</div>
                            <h6>{deviceControl.label}</h6>
                          </div>
                        );
                      })}
                      {availableConnectors.map(
                        (connector) =>
                          (connector?.status === allStatus.CHARGING ||
                            connector?.status === allStatus.PREPARING) && (
                            <div
                              className={`device-control ${isDeviceOffline ? 'disabled' : ''}`}
                              key={connector.connectorId}
                              role="presentation"
                              title={
                                isDeviceOffline
                                  ? t(
                                      'unableToTriggerOffline',
                                      'Unable to trigger this action because device is offline',
                                    )
                                  : ''
                              }
                              onClick={() => {
                                if (isDeviceOffline) {
                                  dispatch(
                                    setNotificationMessage({
                                      status: true,
                                      message: t(
                                        'unableToTriggerOffline',
                                        'Unable to trigger this action because device is offline',
                                      ),
                                      type: 'error',
                                      code: 0,
                                    }),
                                  );
                                  return;
                                }
                                handleConnectorClickEvent(
                                  connector.connectorId,
                                  connector?.status === allStatus.CHARGING ? 'stop' : 'start',
                                );
                              }}>
                              <div className="device-control-icon">
                                {connector?.status === allStatus.CHARGING && <StopCharging />}
                                {connector?.status === allStatus.PREPARING && <StartCharging />}
                              </div>
                              <h6>
                                {t('connector')} {connector.connectorId}
                              </h6>
                            </div>
                          ),
                      )}
                    </div>
                  </div>
                );
                break;
              case 'configuration':
                dataToShow = (
                  <div className="device-configuration-wrap">
                    {selectedDeviceGeneral?.configurationStatus?.length ? (
                      <div className="device-configuration-list">
                        {selectedDeviceGeneral.configurationStatus.map((config, index) => (
                          <div
                            className="each-configuration-detail"
                            key={
                              (typeof config._id === 'string' ? config._id : config._id?.$oid) ||
                              `${config.key}-${index}`
                            }>
                            <div className="config-key-value">
                              <h6>{config.key}</h6>
                              <h1>{config.value}</h1>
                            </div>
                            <span className={`status-badge ${getConfigStatusClass(config.status)}`}>
                              {config.status}
                            </span>
                            <div className="config-timestamps">
                              <p>
                                {t('requestedAt', 'Requested')}:{' '}
                                {FormatUtcString(
                                  ConvertUTCtoHHMM(extractDateString(config.requestedAt)),
                                )}
                                , {ConvertUTCtoDate(extractDateString(config.requestedAt))}
                              </p>
                              <p>
                                {t('respondedAt', 'Responded')}:{' '}
                                {FormatUtcString(
                                  ConvertUTCtoHHMM(extractDateString(config.respondedAt)),
                                )}
                                , {ConvertUTCtoDate(extractDateString(config.respondedAt))}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-configuration-text">
                        {t('noConfigurationFound', 'No configuration data available')}
                      </p>
                    )}
                  </div>
                );
                break;

              default:
                break;
            }
          }
          return dataToShow;
        })()}
      </div>
    </div>
  );
}

export default DeviceContentTabs;
